import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Folder,
  Upload,
  ArrowRight,
  FileText,
  Loader2,
  Download,
  ExternalLink,
  Trash2,
  Database,
  CheckCircle2,
  Circle,
  RefreshCw,
  Search,
  AlertTriangle,
  Package,
  ChevronRight,
  Eye,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";
import FilePreviewDialog from "../components/FilePreviewDialog";

const MAX_ARTIFACTS_FETCH_LIMIT = 10000;

const TYPE_BUCKETS = {
  structured: { label: "Structured Data", exts: ["csv", "json", "tsv", "xlsx", "xls", "parquet"] },
  documents: { label: "Documents", exts: ["pdf", "docx", "doc", "txt", "md", "eml", "html"] },
  logs: { label: "Logs", exts: ["log", "gz", "zip"] },
  images: { label: "Images", exts: ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"] },
};

const STATUS_BUCKETS = {
  ready: { label: "Ready", icon: CheckCircle2, color: "text-emerald-600", dot: "bg-emerald-500" },
  processing: { label: "Processing", icon: Circle, color: "text-amber-600", dot: "bg-amber-500 animate-pulse" },
  failed: { label: "Issues", icon: AlertTriangle, color: "text-red-600", dot: "bg-red-500" },
};

const PENDING_STATES = ["ingesting", "processing", "embedding", "queued"];

function isArtifactActive(artifact) {
  const filename = String(artifact.filename || "").toLowerCase();
  const isStructured = artifact.is_structured ?? (filename.endsWith(".csv") || filename.endsWith(".json"));
  const status = String(artifact.status || "").toLowerCase();
  const rag = String(artifact.rag_indexed || "").toLowerCase();

  // 1. Hard completion guard: if status or rag_indexed are completed/ready, the document is done
  if ((status === "completed" || status === "ready") && (rag === "completed" || rag === "failed" || !rag)) {
    return false;
  }

  // 2. Failure guard: failed artifacts are done (in failed state, not active)
  if (status === "failed" || rag === "failed") {
    return false;
  }

  // 3. Check pending flags on status or rag_indexed
  if (PENDING_STATES.includes(status)) return true;
  if (PENDING_STATES.includes(rag)) return true;

  // 4. Ingestion step check applies ONLY to structured CSV/JSON files
  if (isStructured) {
    const step = String(artifact.ingestion_progress?.step || "").toLowerCase();
    if (PENDING_STATES.includes(step)) return true;
  }

  if (artifact.jobs && typeof artifact.jobs === "object" && "indexing_job_id" in artifact.jobs) {
    if (rag !== "completed" && rag !== "failed") return true;
  }

  return false;
}

function getStatusBucket(artifact) {
  if (isArtifactActive(artifact)) return "processing";
  const status = String(artifact.status || "uploaded").toLowerCase();
  const rag = String(artifact.rag_indexed || "").toLowerCase();

  if (status === "failed" || rag === "failed") return "failed";
  if (status === "ready" || status === "completed" || rag === "completed") return "ready";

  return "processing";
}

function getTypeBucket(artifact) {
  const ext = String(artifact.filename || "").split(".").pop().toLowerCase();
  for (const [key, bucket] of Object.entries(TYPE_BUCKETS)) {
    if (bucket.exts.includes(ext)) return key;
  }
  return "documents";
}

function getSourceBucket(artifact) {
  return artifact.import_source === "s3_sync" ? "s3" : "uploaded";
}

function formatSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return "-";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${bytes}B`;
}

function formatExt(filename) {
  const ext = String(filename || "").split(".").pop();
  return ext ? ext.toUpperCase() : "FILE";
}

export default function Phase5Artifacts() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun } = useAudit();
  const fileInputRef = useRef(null);
  const [artifacts, setArtifacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [availableDownloads, setAvailableDownloads] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const transactionDataset = auditRun?.transaction_dataset;

  const auditType = String(auditRun?.audit_type || auditRun?.context?.audit_type || "").toUpperCase();
  const auditDomain = String(auditRun?.context?.audit_domain || "").toLowerCase();
  const auditName = String(auditRun?.name || "").toUpperCase();
  const isRetailRun = auditDomain === "retail";
  const isRetailDemo =
    isRetailRun &&
    (
      auditName.includes("SMART BAZAAR") ||
      auditType.includes("SHRINK") ||
      auditType.includes("LOSS PREVENTION") ||
      auditType.includes("PROCUREMENT") ||
      auditType.includes("STORE OPERATIONS")
    );

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const [s3ImportOpen, setS3ImportOpen] = useState(false);
  const [s3Payload, setS3Payload] = useState({
    bucket: "",
    prefix: "",
    dataset_type: "artifact",
    aws_access_key: "",
    aws_secret_key: "",
    aws_region: ""
  });
  const [isImporting, setIsImporting] = useState(false);
  const [previewArtifact, setPreviewArtifact] = useState(null);

  const fetchArtifacts = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(
        `/audit-runs/${auditRun.id}/artifacts?limit=${MAX_ARTIFACTS_FETCH_LIMIT}`
      );
      const list = data.artifacts || data;
      setArtifacts(list);
      setTotalCount(data.total !== undefined ? data.total : list.length);
      setCurrentPage(1);
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
    }
  }, [auditRun?.id]);

  const fetchAvailableDownloads = useCallback(async () => {
    try {
      const { data } = await API.get("/available-downloads");
      setAvailableDownloads(data.downloads || []);
    } catch (error) {
      console.error("Failed to fetch downloads:", error);
    }
  }, []);

  useEffect(() => {
    fetchArtifacts();
    fetchAvailableDownloads();
  }, [fetchArtifacts, fetchAvailableDownloads]);

  const fetchTransactionSummary = useCallback(async () => {
    if (!auditRun?.id) return null;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/transactions/summary`);
      setTransactionSummary(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch transaction summary:", error);
      return null;
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchTransactionSummary();
  }, [fetchTransactionSummary, transactionDataset?.status]);

  // Polling loop to refresh status when any artifacts are processing/ingesting
  // (status OR ingestion_progress.step OR rag_indexed — mirrors backend truth).
  useEffect(() => {
    const summaryPending = transactionSummary
      ? Number(transactionSummary.pending ?? transactionSummary.ingesting ?? 0)
      : 0;
    const hasActiveTasks =
      artifacts.some(isArtifactActive) ||
      summaryPending > 0 ||
      (transactionSummary && transactionSummary.status === "ingesting");

    if (hasActiveTasks) {
      const interval = setInterval(() => {
        fetchArtifacts();
        fetchTransactionSummary();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [artifacts, transactionSummary, fetchArtifacts, fetchTransactionSummary]);

  const summaryPendingCount = transactionSummary
    ? Number(transactionSummary.pending ?? transactionSummary.ingesting ?? 0)
    : artifacts.filter(isArtifactActive).length;
  const artifactsBlocked = artifacts.length > 0 && summaryPendingCount > 0;

  const sha256Fallback = (buffer) => {
    const words = [];
    const view = new DataView(buffer);
    for (let i = 0; i < view.byteLength; i += 4) {
      if (i + 4 <= view.byteLength) {
        words.push(view.getUint32(i, false));
      } else {
        let word = 0;
        for (let j = 0; j < view.byteLength - i; j++) {
          word |= view.getUint8(i + j) << (24 - j * 8);
        }
        words.push(word);
      }
    }

    const bitLength = buffer.byteLength * 8;
    words[view.byteLength >> 2] |= 0x80 << (24 - (view.byteLength & 3) * 8);

    const blocksCount = ((words.length + 2) >> 4) + 1;
    const blocks = new Uint32Array(blocksCount * 16);
    for (let i = 0; i < words.length; i++) {
      blocks[i] = words[i];
    }
    blocks[blocks.length - 1] = bitLength;

    const h = new Uint32Array([
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
      0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ]);

    const k = new Uint32Array([
      0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
      0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
      0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
      0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
      0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
      0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
    ]);

    const w = new Uint32Array(64);

    const rightRotate = (value, amount) => {
      return (value >>> amount) | (value << (32 - amount));
    };

    for (let i = 0; i < blocks.length; i += 16) {
      let a = h[0];
      let b = h[1];
      let c = h[2];
      let d = h[3];
      let e = h[4];
      let f = h[5];
      let g = h[6];
      let h_val = h[7];

      for (let t = 0; t < 64; t++) {
        if (t < 16) {
          w[t] = blocks[i + t];
        } else {
          const s0 = (rightRotate(w[t - 15], 7) ^ rightRotate(w[t - 15], 18) ^ (w[t - 15] >>> 3));
          const s1 = (rightRotate(w[t - 2], 17) ^ rightRotate(w[t - 2], 19) ^ (w[t - 2] >>> 10));
          w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
        }

        const temp1 = (h_val + (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) + ((e & f) ^ (~e & g)) + k[t] + w[t]) | 0;
        const temp2 = ((rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;

        h_val = g;
        g = f;
        f = e;
        e = (d + temp1) | 0;
        d = c;
        c = b;
        b = a;
        a = (temp1 + temp2) | 0;
      }

      h[0] = (h[0] + a) | 0;
      h[1] = (h[1] + b) | 0;
      h[2] = (h[2] + c) | 0;
      h[3] = (h[3] + d) | 0;
      h[4] = (h[4] + e) | 0;
      h[5] = (h[5] + f) | 0;
      h[6] = (h[6] + g) | 0;
      h[7] = (h[7] + h_val) | 0;
    }

    const hexParts = [];
    for (let i = 0; i < 8; i++) {
      let hex = h[i].toString(16);
      while (hex.length < 8) {
        hex = "0" + hex;
      }
      hexParts.push(hex);
    }
    return hexParts.join("");
  };

  const computeSHA256 = async (file) => {
    const buffer = await file.arrayBuffer();
    if (window.crypto && window.crypto.subtle) {
      try {
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } catch (err) {
        console.warn("crypto.subtle.digest failed, falling back to pure JS SHA-256", err);
      }
    }
    return sha256Fallback(buffer);
  };

  const uploadFilesList = async (files) => {
    if (!files || !files.length || !auditRun?.id) return;

    setUploading(true);
    let completedCount = 0;
    const confirmedArtifacts = [];
    const failedFilenames = [];

    try {
      // 1. Request presigned URLs via /bulk-request
      const { data: { uploads } } = await API.post(`/audit-runs/${auditRun.id}/artifacts/bulk-request`, {
        files: files.map(f => ({ filename: f.name, dataset_type: "artifact" }))
      });

      const CONCURRENCY = 15;
      const queue = uploads.map((u, index) => ({ ...u, file: files[index] }));

      const worker = async () => {
        while (queue.length > 0) {
          const item = queue.shift();
          if (!item) continue;

          let attempts = 0;
          let success = false;

          while (attempts < 2 && !success) {
            try {
              attempts++;
              const fileHash = await computeSHA256(item.file);
              const formData = new FormData();
              Object.entries(item.presigned?.fields || {}).forEach(([k, v]) => {
                formData.append(k, v);
              });
              formData.append("file", item.file);

              const response = await fetch(item.presigned.url, {
                method: 'POST',
                body: formData,
              });

              if (!response.ok) throw new Error(`S3 responded with ${response.status}`);

              confirmedArtifacts.push({
                filename: item.filename,
                s3_url: item.presigned.s3_url,
                file_size: item.file.size,
                hash: fileHash,
                dataset_type: item.dataset_type
              });
              completedCount += 1;
              success = true;
            } catch (error) {
              if (attempts >= 2) {
                failedFilenames.push(item.filename);
                console.error(`Final failure for ${item.filename}:`, error);
              } else {
                console.warn(`Retrying ${item.filename} (Attempt ${attempts + 1})...`);
              }
            }
          }
        }
      };

      const workers = Array(Math.min(CONCURRENCY, uploads.length)).fill(null).map(worker);
      await Promise.all(workers);

      // 2. Confirm all successful uploads to backend in one batch via /bulk-confirm
      if (confirmedArtifacts.length > 0) {
        await API.post(`/audit-runs/${auditRun.id}/artifacts/bulk-confirm`, {
          artifacts: confirmedArtifacts
        });
        toast.success(`Successfully uploaded and indexed ${completedCount} artifacts.`);
      }
    } catch (error) {
      toast.error("Bulk upload initialization failed.");
      console.error(error);
    } finally {
      setUploading(false);
      await fetchArtifacts();
      if (failedFilenames.length > 0) {
        toast.error(`${failedFilenames.length} files failed: ${failedFilenames.slice(0, 3).join(", ")}${failedFilenames.length > 3 ? "..." : ""}`);
      }
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    await uploadFilesList(files);
    if (event.target) event.target.value = "";
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files || []);
    await uploadFilesList(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleContinue = async () => {
    if (artifactsBlocked) {
      const names = Array.isArray(transactionSummary?.pending_files) && transactionSummary.pending_files.length > 0
        ? transactionSummary.pending_files.slice(0, 3).map((f) => (typeof f === "string" ? f : f?.filename)).filter(Boolean).join(", ")
        : null;
      toast.warning(
        `⏳ ${summaryPendingCount} artifact(s) still processing. Wait for indexing to complete before continuing.` +
        (names ? ` (e.g. ${names})` : "")
      );
      return;
    }
    await advancePhase(7);
    navigate("/phase/7", { state: { samplingComplete: true } });
    toast.success("Proceeding to Evidence Evaluation");
  };

  const handleSync = async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/artifacts/sync`);
      if (data.removed_count > 0) {
        toast.success(`Sync complete. Removed ${data.removed_count} missing artifacts.`);
        await fetchArtifacts();
      } else {
        toast.info("Database is already in sync with S3.");
      }
    } catch (error) {
      toast.error("Failed to sync artifacts with S3.");
    }
  };

  const handleS3Import = async () => {
    if (!auditRun?.id || !s3Payload.bucket || !s3Payload.aws_access_key || !s3Payload.aws_secret_key) {
      toast.error("Bucket, Access Key, and Secret Key are required.");
      return;
    }

    const trimmedPayload = {
      bucket: (s3Payload.bucket || "").trim(),
      prefix: (s3Payload.prefix || "").trim(),
      aws_access_key: (s3Payload.aws_access_key || "").trim(),
      aws_secret_key: (s3Payload.aws_secret_key || "").trim(),
      aws_region: (s3Payload.aws_region || "us-east-1").trim(),
    };

    setIsImporting(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/s3-import`, trimmedPayload);
      if (data.count > 0) {
        toast.success(`Import started for ${data.count} files from S3.`);
        setS3ImportOpen(false);
        await fetchArtifacts();
      } else {
        toast.info(data.message || "No new files found to import.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "S3 Import failed. Check credentials and path.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleDelete = async (artifactId, filename) => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.delete(`/audit-runs/${auditRun.id}/artifacts/${artifactId}`);
      toast.success(
        data?.deleted_rows
          ? `Deleted: ${filename} (${data.deleted_rows} rows removed)`
          : `Deleted: ${filename}`
      );
      await Promise.all([fetchArtifacts(), fetchTransactionSummary(), refreshRun()]);
    } catch (error) {
      toast.error("Failed to delete artifact");
    }
  };

  const counts = useMemo(() => {
    const result = {
      ready: 0,
      processing: 0,
      failed: 0,
      types: { structured: 0, documents: 0, logs: 0, images: 0 },
      sources: { uploaded: 0, s3: 0 },
    };
    artifacts.forEach((a) => {
      result[getStatusBucket(a)] += 1;
      result.types[getTypeBucket(a)] += 1;
      result.sources[getSourceBucket(a)] += 1;
    });
    return result;
  }, [artifacts]);

  const filteredArtifacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return artifacts.filter((a) => {
      const matchesSearch = !q || String(a.filename || "").toLowerCase().includes(q);
      const matchesStatus = statusFilter === "all" || getStatusBucket(a) === statusFilter;
      const matchesType = typeFilter === "all" || getTypeBucket(a) === typeFilter;
      const matchesSource = sourceFilter === "all" || getSourceBucket(a) === sourceFilter;
      return matchesSearch && matchesStatus && matchesType && matchesSource;
    });
  }, [artifacts, search, statusFilter, typeFilter, sourceFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredArtifacts.length / ITEMS_PER_PAGE));
  const currentArtifacts = filteredArtifacts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setSourceFilter("all");
  };

  const SidebarRow = ({ label, count, active, onClick, icon: Icon, iconClass }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-[13px] transition-colors ${
        active ? "bg-primary/10 font-semibold text-primary" : "text-foreground hover:bg-muted/40"
      }`}
    >
      <span className="flex min-w-0 items-center gap-2">
        {Icon && <Icon className={`h-3.5 w-3.5 shrink-0 ${iconClass || ""}`} />}
        <span className="min-w-0 truncate">{label}</span>
      </span>
      <span className="font-mono text-[11px] text-muted-foreground">{count}</span>
    </button>
  );

  return (
    <PageLayout
      icon={Folder}
      title="Phase 5 – Artifact Collection"
      subtitle="Collect and validate evidence for controls in audit scope."
      actions={
        <>
          <Button
            variant="outline"
            onClick={handleSync}
            data-testid="sync-artifacts-btn"
            className="sm:shrink-0"
            title="Reconcile with physical S3 storage"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Sync Local
          </Button>
          <Dialog open={s3ImportOpen} onOpenChange={setS3ImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="sm:shrink-0 bg-primary/5 hover:bg-primary/10 border-primary/20">
                <Database className="w-4 h-4 mr-2 text-primary" /> Import from S3
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Import from S3 Folder</DialogTitle>
                <DialogDescription>
                  AuditOS will scan the specified S3 bucket and prefix, creating artifact records for all found files.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="bucket">S3 Bucket <span className="text-red-500">*</span></Label>
                  <Input
                    id="bucket"
                    placeholder="e.g. my-audit-evidence"
                    value={s3Payload.bucket}
                    onChange={(e) => setS3Payload({ ...s3Payload, bucket: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="prefix">Prefix (Folder Path)</Label>
                  <Input
                    id="prefix"
                    placeholder="e.g. clients/acme/2024-q1/"
                    value={s3Payload.prefix}
                    onChange={(e) => setS3Payload({ ...s3Payload, prefix: e.target.value })}
                  />
                </div>
                <div className="grid gap-3 pt-2 border-t border-border/40 mt-2">
                  <div className="grid gap-2">
                    <Label htmlFor="access_key">AWS Access Key <span className="text-red-500">*</span></Label>
                    <Input
                      id="access_key"
                      placeholder="AKIA..."
                      type="password"
                      value={s3Payload.aws_access_key}
                      onChange={(e) => setS3Payload({ ...s3Payload, aws_access_key: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="secret_key">AWS Secret Key <span className="text-red-500">*</span></Label>
                    <Input
                      id="secret_key"
                      placeholder="Secret Key"
                      type="password"
                      value={s3Payload.aws_secret_key}
                      onChange={(e) => setS3Payload({ ...s3Payload, aws_secret_key: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="region">AWS Region</Label>
                    <Input
                      id="region"
                      placeholder="us-east-1"
                      value={s3Payload.aws_region}
                      onChange={(e) => setS3Payload({ ...s3Payload, aws_region: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleS3Import}
                  disabled={isImporting || !s3Payload.bucket || !s3Payload.aws_access_key || !s3Payload.aws_secret_key}
                  className="w-full"
                >
                  {isImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                  {isImporting ? "Processing S3 Import..." : "Import Folder"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            onClick={handleContinue}
            data-testid="continue-to-sampling-top-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-4 shadow-md shadow-emerald-600/30 active:scale-95 border-0"
          >
            Continue to Evidence <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </>
      }
      data-testid="phase-5"
    >
      <div className="flex h-full flex-col gap-4">
        {/* Summary strip */}
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
              <Package className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xl font-semibold leading-none">{totalCount}</div>
              <div className="mt-1 text-xs text-muted-foreground">Artifacts</div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xl font-semibold leading-none">{counts.ready}</div>
              <div className="mt-1 text-xs text-muted-foreground">Ready</div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Loader2 className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xl font-semibold leading-none">{counts.processing}</div>
              <div className="mt-1 text-xs text-muted-foreground">Processing</div>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <div className="text-xl font-semibold leading-none">{counts.failed}</div>
              <div className="mt-1 text-xs text-muted-foreground">Failed / Issues</div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artifacts..."
              className="h-9 pl-9 pr-3 text-xs"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-xs"
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="ready">Ready</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed / Issues</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-xs"
            aria-label="Filter by type"
          >
            <option value="all">All Types</option>
            <option value="structured">Structured Data</option>
            <option value="documents">Documents</option>
            <option value="logs">Logs</option>
            <option value="images">Images</option>
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-xs"
            aria-label="Filter by source"
          >
            <option value="all">All Sources</option>
            <option value="uploaded">Uploaded</option>
            <option value="s3">S3</option>
          </select>
        </div>

        {/* Two-pane body */}
        <div className="grid min-h-0 flex-1 grid-cols-[230px_minmax(0,1fr)] gap-4 overflow-hidden">
          {/* Sidebar */}
          <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card">
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Evidence Sources
              </p>
              <SidebarRow
                label="All Artifacts"
                count={totalCount}
                active={sourceFilter === "all" && statusFilter === "all" && typeFilter === "all"}
                onClick={resetFilters}
                icon={Package}
              />
              <SidebarRow
                label="Uploaded"
                count={counts.sources.uploaded}
                active={sourceFilter === "uploaded"}
                onClick={() => setSourceFilter((c) => (c === "uploaded" ? "all" : "uploaded"))}
                icon={Upload}
              />
              <SidebarRow
                label="S3"
                count={counts.sources.s3}
                active={sourceFilter === "s3"}
                onClick={() => setSourceFilter((c) => (c === "s3" ? "all" : "s3"))}
                icon={Database}
              />

              <div className="my-2 border-t border-border/50" />

              <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Artifact Type
              </p>
              {Object.entries(TYPE_BUCKETS).map(([key, bucket]) => (
                <SidebarRow
                  key={key}
                  label={bucket.label}
                  count={counts.types[key]}
                  active={typeFilter === key}
                  onClick={() => setTypeFilter((c) => (c === key ? "all" : key))}
                  icon={key === "structured" ? Database : key === "images" ? Upload : FileText}
                />
              ))}

              <div className="my-2 border-t border-border/50" />

              <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </p>
              {Object.entries(STATUS_BUCKETS).map(([key, bucket]) => (
                <SidebarRow
                  key={key}
                  label={bucket.label}
                  count={counts[key]}
                  active={statusFilter === key}
                  onClick={() => setStatusFilter((c) => (c === key ? "all" : key))}
                  icon={bucket.icon}
                  iconClass={bucket.color}
                />
              ))}
            </div>
          </aside>

          {/* Artifacts pane */}
          <section
            className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card transition-colors hover:border-emerald-500/40"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div className="flex shrink-0 items-center justify-between border-b px-4 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Artifacts
              </p>
              <Badge variant="secondary" className="text-[10px] font-mono">
                {filteredArtifacts.length} {filteredArtifacts.length === 1 ? "artifact" : "artifacts"}
              </Badge>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Demo / download helper cards */}
              {/* {isRelianceRetailDemo && (
                <div className="border-b border-border/40 bg-gradient-to-r from-amber-50 via-primary/5 to-rose-50 p-4 dark:from-amber-400/10 dark:via-slate-900 dark:to-rose-400/10">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <ShieldAlert className="h-4 w-4 text-orange-600 dark:text-orange-300" />
                    Reliance Retail Demo
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {RELIANCE_DEMO_CHECKLIST.map((item) => (
                      <p key={item} className="text-xs text-muted-foreground">- {item}</p>
                    ))}
                  </div>
                </div>
              )} */}

              {/* {isRelianceRetailDemo && (
                <div className="space-y-3 border-b border-border/40 p-4">
                  <p className="text-xs font-medium flex items-center gap-2">
                    <Database className="h-3.5 w-3.5" /> Structured Dataset Status
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Filename</p>
                      <p className="mt-1 font-mono text-xs break-all">{transactionDataset?.filename || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Dataset Type</p>
                      <p className="mt-1 text-xs">{transactionDataset?.dataset_type || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${transactionDataset?.status === "ready" ? "bg-emerald-500" : transactionDataset?.status === "failed" ? "bg-red-500" : "bg-amber-500 animate-pulse"}`} />
                        <span className="font-mono text-xs capitalize">{transactionDataset?.status || "not loaded"}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Rows</p>
                      <p className="mt-1 font-mono text-xs">{transactionDataset?.row_count ?? transactionSummary?.total_records ?? 0}</p>
                    </div>
                  </div>
                </div>
              )} */}

              {availableDownloads.length > 0 && (
                <div className="border-b border-border/40 p-4">
                  <p className="text-xs font-medium flex items-center gap-2 mb-3">
                    <Download className="h-3.5 w-3.5" /> Sample Artifact Packages
                  </p>
                  <div className="grid gap-2">
                    {availableDownloads.map((downloadItem, index) => (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_BACKEND_URL}${downloadItem.url}`}
                        download
                        className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 transition-colors group"
                        data-testid={`download-${downloadItem.filename}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 truncate text-sm font-medium">{downloadItem.filename}</span>
                          <span className="text-xs text-muted-foreground">{downloadItem.size_kb} KB</span>
                        </div>
                        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Artifact table */}
              {artifacts.length > 0 && (
                <div className="p-4 border-b border-border/40">
                  {artifacts.some(isArtifactActive) || summaryPendingCount > 0 || (transactionSummary && transactionSummary.status === "ingesting") ? (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-500/10 p-3.5 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/5 dark:text-amber-300">
                      <Loader2 className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin shrink-0" />
                      <div className="text-xs">
                        <p className="font-semibold">Processing Uploaded Files</p>
                        <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                          AuditOS is profiling columns, indexing text, and syncing domain entities. Please wait.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-500/10 p-3.5 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/5 dark:text-emerald-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <div className="text-xs">
                        <p className="font-semibold">All Artifacts Fully Processed</p>
                        <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-0.5">
                          All submitted evidence has been reviewed and catalogued. You may proceed to the next phase.                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {filteredArtifacts.length === 0 ? (
                <div className="flex min-h-[280px] flex-col items-center justify-center text-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                    <Folder className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">
                    {artifacts.length === 0 ? "No artifacts collected yet" : "No matching artifacts"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {artifacts.length === 0
                      ? "Upload evidence files to begin artifact collection."
                      : "Try changing your search or filters."}
                  </p>
                  {artifacts.length === 0 && (
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 cursor-pointer text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!auditRun || uploading}
                      >
                        <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload files
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border/40 bg-muted/20">
                      <th className="w-10 px-3 py-2.5" />
                      <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Filename</th>
                      <th className="w-24 px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                      <th className="w-36 px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="w-20 px-3 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Size</th>
                      <th className="w-12 px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {currentArtifacts.map((artifact) => {
                      const bucket = getStatusBucket(artifact);
                      const meta = STATUS_BUCKETS[bucket];
                      return (
                        <tr key={artifact.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                          <td className="px-3 py-2.5">
                            <span className={`mx-1.5 block h-2 w-2 rounded-full ${meta.dot}`} />
                          </td>
                          <td className="py-2.5">
                            <div className="flex items-center gap-2.5">
                              <FileText className="h-4 w-4 shrink-0 text-muted-foreground/70" />
                              <span className="font-mono text-xs font-medium text-foreground/90">{artifact.filename}</span>
                            </div>
                          </td>
                          <td className="py-2.5">
                            <Badge variant="outline" className="rounded-full border-border/60 bg-muted/40 px-2.5 py-0.5 text-[11px] font-medium text-foreground/80">
                              {formatExt(artifact.filename)}
                            </Badge>
                          </td>
                          <td className="py-2.5">
                            <div className="flex flex-col gap-1">
                              <span className={`flex items-center gap-1.5 text-xs font-semibold capitalize ${meta.color}`}>
                                {bucket === "ready" ? "Ready" : bucket === "failed" ? "Failed" : "Processing"}
                              </span>
                              {artifact.rag_status_detail && bucket !== "ready" && (
                                <span className="text-[10px] text-muted-foreground leading-tight italic">
                                  {artifact.rag_status_detail}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 text-right font-mono text-[11px] text-muted-foreground">
                            {formatSize(artifact.file_size)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 transition-colors"
                              onClick={() => setPreviewArtifact(artifact)}
                              title="Preview file"
                              data-testid={`preview-artifact-${artifact.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-500/70 hover:bg-red-500/10 hover:text-red-600 transition-colors"
                              onClick={() => handleDelete(artifact.id, artifact.filename)}
                              title="Delete artifact"
                              data-testid={`delete-artifact-${artifact.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {filteredArtifacts.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between border-t border-border/40 bg-muted/10 px-4 py-2.5">
                  <div className="text-[11px] text-muted-foreground font-medium">
                    Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredArtifacts.length)} of {filteredArtifacts.length} entries
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px] bg-transparent border-border/60 hover:bg-muted/50"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="text-[11px] font-medium px-2">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px] bg-transparent border-border/60 hover:bg-muted/50"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Bottom bar */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3">
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{totalCount}</span> artifacts ·{" "}
            <span className="font-semibold text-emerald-600">{counts.ready}</span> ready
          </span>

          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              className="hidden"
              accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.txt,.md,.eml,.json,.png,.jpg,.jpeg"
              data-testid="artifact-upload-input"
              disabled={!auditRun || uploading}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-9 cursor-pointer gap-1.5 text-xs"
              disabled={uploading || !auditRun}
              onClick={() => fileInputRef.current?.click()}
              data-testid="artifact-upload-btn"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Uploading..." : "Upload"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 text-xs"
              onClick={() => setS3ImportOpen(true)}
            >
              <Database className="h-3.5 w-3.5" />
              Import S3
            </Button>
            <Button
              size="sm"
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-4 shadow-md shadow-emerald-600/30 active:scale-95 border-0 disabled:opacity-50"
              onClick={handleContinue}
              disabled={artifactsBlocked}
              title={artifactsBlocked ? `Waiting for ${summaryPendingCount} artifact(s) to finish indexing` : "Continue to Evidence"}
              data-testid="phase-5-continue-btn"
            >
              {artifactsBlocked ? `Processing ${summaryPendingCount}...` : <>Continue to Evidence <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </div>
        </div>
      </div>

      <FilePreviewDialog
        open={!!previewArtifact}
        onOpenChange={(open) => {
          if (!open) setPreviewArtifact(null);
        }}
        auditRunId={auditRun?.id}
        item={previewArtifact}
      />
    </PageLayout>
  );
}
